import React from 'react';
import classNameBind from 'classnames/bind';
import Icon from '../Icon';
import Button from '../Button';
import ActionList from '../ActionList/ActionList';
import Popover from '../Popover/Popover';
import Tooltip from '../Tooltip';
import styles from './Breadcrumb.module.css';

const bindClasses = classNameBind.bind(styles);
const HomeIcon = <Icon name="home" color="blue" className={bindClasses('icon')} />;

const TEXT_SHRINK_THRESHOLD = 14;

const Breadcrumb = ({ elements, appearance = 'default', homeIcon = false, makeSecondElementGray = false }) => {
  const popoverItems = (elements.slice(makeSecondElementGray ? 2 : 1, makeSecondElementGray ? -1 : -2)).map((item, index) => ({
    name: item.text,
    id: `popoverId-${index}`,
    onClick: item.onClick || (() => {}),
    text: item.text,
  }));

  const lastItems = elements.length >= 4 ? elements.slice(makeSecondElementGray ? -1 : -2) : elements.slice(-1);

  switch (appearance) {
    case 'compact':
    case 'location':
      return (
        <div
          className={bindClasses('wrapper', `appearance-${appearance}`)}
          data-ppuc-id="Breadcrumb"
        >
          {elements.length > `${makeSecondElementGray ? 4 : 3}` ? (
            <>
              <div
                className={bindClasses('element', { homeIcon }, 'firstElement', { doNotShrink: elements[0].text.length < TEXT_SHRINK_THRESHOLD && !homeIcon })}
                data-ppuc-id="Breadcrumb.element"
              >
                <Tooltip content={elements[0].text}>
                  <Button
                    text={homeIcon ? HomeIcon : elements[0].text}
                    appearance={elements[0].onClick && appearance !== 'location' ? 'simple' : 'subdued'}
                    minimalWidth
                    noHover
                    onClick={elements[0].onClick || (() => {})}
                    noPadding={homeIcon}
                    size="small"
                    setWidth={homeIcon ? '26' : ''}
                    className={bindClasses('breadcrumb-button')}
                  />
                </Tooltip>
              </div>
              {makeSecondElementGray
              && appearance === 'location' && (
              <div
                className={bindClasses('element', { doNotShrink: elements[1].text.length < TEXT_SHRINK_THRESHOLD })}
                data-ppuc-id="Breadcrumb.element"
              >
                <span className={bindClasses('divider')}>/</span>
                <Tooltip content={elements[1].text}>
                  <Button
                    text={elements[1].text}
                    appearance="subdued"
                    minimalWidth
                    noHover
                    onClick={elements[1].onClick || (() => {})}
                    size="small"
                    className={bindClasses('gray', 'breadcrumb-button')}
                  />
                </Tooltip>
              </div>
              )}
              <div
                className={bindClasses('element', 'dotsIcon')}
                data-ppuc-id="Breadcrumb.element"
              >
                {appearance !== 'location' ? <Icon name="chevron-right" color="gray" className={bindClasses('icon')} /> : (
                  <span className={bindClasses('divider')}>/</span>
                )}
                <Popover
                  activatorElement={(
                    <Button
                      text={<Icon name="dots" color={appearance !== 'location' ? 'blue' : 'gray'} className={bindClasses('icon')} />}
                      noHover
                      appearance="simple"
                      size="small"
                      minimalWidth
                      noPadding
                      setWidth="26"
                    />
                  )}
                  childToggle={{ onItemSelected: () => true }}
                  hoverTrigger
                >
                  <ActionList
                    onItemSelected={(item) => { popoverItems.find(el => el.id === item.id).onClick(); }}
                    groups={[
                      {
                        id: 1,
                        items: popoverItems,
                      },
                    ]}
                    appearance="condensed"
                  />
                </Popover>
              </div>
              {appearance !== 'location' && <Icon name="chevron-right" color="gray" className={bindClasses('icon')} />}
              {lastItems.map((el, index) => (
                <div
                  className={bindClasses('element', { doNotShrink: el.text.length < TEXT_SHRINK_THRESHOLD })}
                  key={`bc-${index}`}
                  data-ppuc-id="Breadcrumb.element"
                >
                  {index !== 0 && appearance !== 'location' && <Icon name="chevron-right" color="gray" className={bindClasses('icon')} />}
                  {appearance === 'location' && (
                    <>
                      <span className={bindClasses('divider')}>/</span>
                    </>
                  )}
                  <Tooltip content={el.text}>
                    <Button
                      text={el.text}
                      appearance={el.onClick && appearance !== 'location' ? 'simple' : 'subdued'}
                      minimalWidth
                      noHover
                      onClick={el.onClick || (() => {})}
                      size="small"
                      icon={appearance === 'location' ? 'folder-location' : ''}
                      className={bindClasses('breadcrumb-button')}
                    />
                  </Tooltip>
                </div>
              ))}
            </>
          ) : (elements.map((el, index) => (
            <div
              className={bindClasses('element', { homeIcon: homeIcon && index === 0 }, { firstElement: index === 0 }, { doNotShrink: el.text.length < TEXT_SHRINK_THRESHOLD })}
              key={`bc-${index}`}
              data-ppuc-id="Breadcrumb.element"
            >
              {index !== 0 && appearance !== 'location' && <Icon name="chevron-right" color="gray" className={bindClasses('icon')} />}
              {index !== 0 && appearance === 'location' && (
                <>
                  <span className={bindClasses('divider')}>/</span>
                </>
              )}
              <Tooltip content={el.text}>
                <Button
                  text={index === 0 && homeIcon ? HomeIcon : el.text}
                  appearance={el.onClick && appearance !== 'location' ? 'simple' : 'subdued'}
                  minimalWidth
                  noHover
                  onClick={el.onClick || (() => {})}
                  size="small"
                  noPadding={index === 0}
                  className={bindClasses({ gray: index === 1 && makeSecondElementGray }, 'breadcrumb-button')}
                  icon={(appearance === 'location'
                    && ((homeIcon && index !== 0) || (!homeIcon))
                    && ((makeSecondElementGray && index !== 1)
                    || (!makeSecondElementGray)))
                    ? 'folder-location' : null}
                />
              </Tooltip>
            </div>
          )))}
        </div>
      );

    default:
      return (
        <div
          className={bindClasses('wrapper')}
          data-ppuc-id="Breadcrumb"
        >
          {elements.map((el, index) => (
            <div
              className={bindClasses('element', { withHomeIcon: homeIcon && index === 0 })}
              key={`bc-${index}`}
              data-ppuc-id="Breadcrumb.element"
            >
              {index !== 0 && <Icon name="chevron-right" color="gray" className={bindClasses('icon')} />}
              <Button
                text={index === 0 && homeIcon ? HomeIcon : el.text}
                appearance={el.onClick ? 'simple' : 'subdued'}
                minimalWidth
                noHover
                onClick={el.onClick || (() => {})}
              />
            </div>
          ))}
        </div>
      );
  }
};

export default Breadcrumb;
